import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import { FEATURE_FLAGS_ENABLED, WHITE_LABEL } from 'app/../app.config.json';
import { WhiteLabel } from 'app/types/white-labels';
import { setAppIcon } from '@howincodes/expo-dynamic-app-icon';
import { env } from 'app/lib/env';
import { Platform } from 'react-native';
import { type SpecificRouteProp } from 'app/types/navigation';
import { type ParamListBase, type RouteProp } from '@react-navigation/native';

export function isBlank(value: string) {
  return (isEmpty(value) && !isNumber(value)) || isNaN(value);
}

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

// To be used in FEATURE_FLAGS_ENABLED in app.config.json
// Those are also received from the API in user.featureFlags
// Both definitions need to match!
// Static flags are listed here + in app config FEATURE_FLAGS_ENABLED. Dynamic flags
// (from DB FeatureFlagDefinition.key like 'group_chat') appear *only* as strings inside
// the merged user.featureFlags.enabled (never added to this enum or static fallbacks).
export enum FeatureFlag {
  // Show the optional department/reference name on Mutuo municipal profiles
  additionalName = 'additionalName',

  // (offers or requests) listings that are in fact donations; eg. Partage Agricole (Serres Tessier)
  donations = 'donations',

  // Allow users to duplicate offers from the management screen
  duplicateListings = 'duplicateListings',

  // Offre du défricheur, trial promo (5 offres = free license)
  freeSubPrograms = 'freeSubPrograms',

  // Allow users to invite others (and maybe get something in return)
  invite = 'invite',

  // Allow unauthenticated users to browse listings on the web
  guestAccess = 'guestAccess',

  // Used in SaaS, to limit the user to only one club
  justOneClub = 'justOneClub',

  // Disabled in SaaS where users are orgs/companies - we only show a single 'name' field then
  lastName = 'lastName',

  // When creating (or editing) a listing, show a field to ask for the availability of the object, i.e. configure blocked dates
  listingPromptAvailability = 'listingPromptAvailability',

  // When creating (or editing) a listing, show a field to ask for the estimated price of the object
  listingPromptEstimatedPrice = 'listingPromptEstimatedPrice',

  // Allow users to see the list of members of a group
  listMembers = 'listMembers',

  // When creating (or editing) a listing, show a field to ask for possible loan fees
  loanFee = 'loanFee',

  // Show "Financial Impact" in the Profile screen
  measureImpact = 'measureImpact',

  // popularItems (Offres rapides) are presented by categories
  popularItemsByCats = 'popularItemsByCats',

  // popularItems (Offres rapides) are presented by tags
  popularItemsByTags = 'popularItemsByTags',

  // Allow (optional) activation code during sign-up
  possibleActivationCodeDuringSignup = 'possibleActivationCodeDuringSignup',

  // Include predictions in recommended listings
  predictions = 'predictions',

  // During sign-up, prompt if the user wants to borrow, lend, or both
  promptSharerType = 'promptSharerType',

  // Can publish listings; Public @ Partage Agricole can't
  publishListings = 'publishListings',

  // Ask users to rate their experience after a loan (used server-side only)
  rateAfterLoans = 'rateAfterLoans',

  // Refer someone, get 1 free month (or something else)
  referralPrize = 'referralPrize',

  // Enforce (mandatory) activation code during sign-up
  requireActivationCodeDuringSignup = 'requireActivationCodeDuringSignup',

  // When searching on Babillard, search by user's name too
  searchListingsByUserName = 'searchListingsByUserName',

  // Show "Recommended for you" in the babillard
  showRecommendedForYou = 'showRecommendedForYou',

  // Show similar listings, in a listing's details screen
  similarListings = 'similarListings',

  // Show sharing places on public user profiles
  sharingPlaces = 'sharingPlaces',

  // Show the sales counter on public user profiles
  showSalesOnPublicProfile = 'showSalesOnPublicProfile',

  // Match needs with potential lenders
  conciergerie = 'conciergerie',

  // Single Sign-On (Facebook, Google, Apple) is available to sign-up and log-in
  sso = 'sso',

  // Allow users to become "Super Partageurs"
  superSharer = 'superSharer',

  // Allow users to become Ambassadors / show ambassador features
  ambassador = 'ambassador',

  // New Onboarding flow (2026-02) for Partage Club - maybe we'll use it for other SaaS later?
  useNewOnboarding = 'useNewOnboarding',

  // Can view Manage subscription in Profile
  viewSubscription = 'viewSubscription',

  // Formal French (vouvoiement) — Mutuo only; merges fr-vous.json before fr-mutuo.json
  useFrVous = 'useFrVous',

  // Show "Are dates flexible?" checkbox when proposing loan dates
  flexibleDates = 'flexibleDates',

  // Show streak (daily contribution tracking) in header and profile
  streak = 'streak',

  // Show transaction mode toggle (sell/lend/give) when creating/editing listings
  transactionMode = 'transactionMode',

  // Allow expirable/perishable listings (e.g. food, time-sensitive materials)
  perishableListings = 'perishableListings',

  useActivitySector = 'useActivitySector',

  // "Aide au transport" (help-to-transport) support chat channel
  helpToTransport = 'helpToTransport',

  // Notify users when a new offer matches one of their interests
  interestAlerts = 'interestAlerts',
}

// Dynamic (DB-only) flag keys MUST be registered in DynamicFeatureFlag below so call sites
// get type-safety and we have a single source of truth shared with the API (mirrored in
// api/src/feature-flags/feature-flags.service.ts). Keep the two definitions in sync.
export enum DynamicFeatureFlag {
  // Gate the swipe-based quick-offer hub in Partage Club and Partake
  OfferHub = 'offer_hub',

  // Gate activity summaries on the landing page and management overview
  ActivitySummary = 'activity_summary',

  // Gate the community social feed and creation of community posts
  SocialFeed = 'social_feed',

  // Gate creation of several listings from photos in one flow
  BulkListingCreation = 'bulk_listing_creation',

  // Gate group conversation creation in chat
  // Deprecated on 2026-07-14; all users (and white-labels) now have access to group chats
  // GroupChat = 'group_chat',

  // Gate linking one of the lender's offers to a request listing chat
  // Deprecated since 2026-07-30; all users (and white-labels) now have access to this feature
  // @TODO Remove this and all usage (server side too?) once we're sure we won't want to disable it in the future
  LinkOfferToRequestChat = 'link_offer_to_request_chat',

  // Gate searching the listings catalog on a map
  SearchCatalogOnMap = 'search_catalog_on_map',

  // Gate AI-generated replies in member support conversations
  SupportBot = 'support_bot',
}

export const isFeatureEnabled = (
  featureFlag: FeatureFlag | DynamicFeatureFlag,
  userTypeConfig?: null | { enabled?: (FeatureFlag | DynamicFeatureFlag | string)[] },
): boolean => (userTypeConfig?.enabled ?? FEATURE_FLAGS_ENABLED).includes(featureFlag);
export const isFeatureDisabled = (
  featureFlag: FeatureFlag | DynamicFeatureFlag,
  userTypeConfig?: null | { enabled?: (FeatureFlag | DynamicFeatureFlag | string)[] },
): boolean => !isFeatureEnabled(featureFlag, userTypeConfig);

export const getDistanceBetweenCoords = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000; // Radius of the earth in m
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const deg2rad = (deg: number) => deg * (Math.PI / 180);

export const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

export const isDarkColor = (color: string) => {
  if (color.length === 4) {
    color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
  }
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  const { r, g, b } = {
    r: parseInt(result?.[1] ?? '00', 16),
    g: parseInt(result?.[2] ?? '00', 16),
    b: parseInt(result?.[3] ?? '00', 16),
  };
  const lightness = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
  return lightness < 90;
};

export const changeAppIcon = () => {
  if (Platform.OS !== 'ios') return; // Seems to be causing problems on Android - disabled for now
  const testEnv = env.APP_ENV !== 'production';
  if (testEnv) return;
  if (isWhiteLabel(WhiteLabel.SerresTessier)) {
    // Different icon per season
    const d = new Date();
    let month = (d.getMonth() + 1).toString();
    let day = d.getDate().toString();
    if (month.length < 2) {
      month = `0${month}`;
    }
    if (day.length < 2) {
      day = `0${day}`;
    }
    const dm = parseInt(`${month}${day}`);
    if (dm >= 1221 || dm < 321) {
      console.log('setAppIcon: serrestessier_winter');
      setAppIcon('serrestessier_winter');
    } else if (dm >= 921) {
      console.log('setAppIcon: serrestessier_autumn');
      setAppIcon('serrestessier_autumn');
    } else if (dm >= 621) {
      console.log('setAppIcon: serrestessier_summer');
      setAppIcon('serrestessier_summer');
    } else {
      console.log('setAppIcon: serrestessier_spring');
      setAppIcon('serrestessier_spring');
    }
  }
};

export function isContactInformationRoute(
  route: RouteProp<ParamListBase> | undefined,
): route is SpecificRouteProp<'contact-information'> {
  return route?.name === 'contact-information';
}

export function isChooseUserTypeRoute(
  route: RouteProp<ParamListBase> | undefined,
): route is SpecificRouteProp<'chooseUserType'> {
  return route?.name === 'chooseUserType';
}

export function isAllListingsRoute(
  route: RouteProp<ParamListBase> | undefined,
): route is SpecificRouteProp<'all-listings'> {
  return route?.name === 'all-listings';
}

export function isLoanRoute(route: RouteProp<ParamListBase> | undefined): route is SpecificRouteProp<'my-loans'> {
  return route?.name === 'my-loans';
}

// Usage:
// - isNotWhiteLabel() === true if WHITE_LABEL is falsy, i.e. if the app is Partage Club (core)
// - isWhiteLabel() === true if WHITE_LABEL is truthy, i.e. is the app is NOT Partage Club (core)
// - isWhiteLabel(WhiteLabel.XYZ) === true if WHITE_LABEL === WhiteLabel.XYZ, i.e. is the app is XYZ (Refrain, Partage Agricole/Serres Tessier, etc.)
export function isWhiteLabel(whichWhiteLabel?: WhiteLabel) {
  if (whichWhiteLabel === undefined) {
    return !!WHITE_LABEL;
  }
  return (WHITE_LABEL as WhiteLabel) === whichWhiteLabel;
}
export function isNotWhiteLabel() {
  return !WHITE_LABEL;
}

export function canUseInterests(): boolean {
  return !isWhiteLabel(WhiteLabel.Refrain);
}

export function isAnyWhiteLabel(): boolean {
  return (
    isWhiteLabel(WhiteLabel.Mutuo) ||
    isWhiteLabel(WhiteLabel.Refrain) ||
    isWhiteLabel(WhiteLabel.SerresTessier) ||
    isWhiteLabel(WhiteLabel.HauteGaspesie)
  );
}

export function getShareListingUrl(listingId: string) {
  const whiteLabelPrefix = isWhiteLabel() ? `.${WHITE_LABEL}` : '';
  const envPrefix = env.APP_ENV === 'production' ? '' : '.dev';
  return `https://app${whiteLabelPrefix}${envPrefix}.partage.club/listings/${listingId}`;
}

export function getShareUserProfileUrl(userId: string) {
  const whiteLabelPrefix = isWhiteLabel() ? `.${WHITE_LABEL}` : '';
  const envPrefix = env.APP_ENV === 'production' ? '' : '.dev';
  return `https://app${whiteLabelPrefix}${envPrefix}.partage.club/userProfileModal/neighbour/${userId}`;
}

const SHARED_LISTING_APP_URL_REGEX =
  /(?:partageclub:\/\/\/listing\/|app(?:\.[a-z0-9-]+)?(?:\.dev)?\.partage\.club\/listings\/)([0-9a-fA-F-]{36})(?:[/?#]|$)/;
const SHARED_USER_PROFILE_APP_URL_REGEX =
  /(?:partageclub:\/\/\/user\/|app(?:\.[a-z0-9-]+)?(?:\.dev)?\.partage\.club\/userProfileModal\/neighbour\/)([0-9a-fA-F-]{36})(?:[/?#]|$)/;

export function getSharedListingIdFromAppUrl(url?: string) {
  return url ? SHARED_LISTING_APP_URL_REGEX.exec(url)?.[1] : undefined;
}

export function getSharedUserProfileIdFromAppUrl(url?: string) {
  return url ? SHARED_USER_PROFILE_APP_URL_REGEX.exec(url)?.[1] : undefined;
}
